import { useEffect, type RefObject } from 'react';

/**
 * Trap keyboard focus within a container while it is active.
 *
 * Used by the {@link RowPeekDrawer} (and reusable for any future
 * modal/drawer primitives) to satisfy the WAI-ARIA Authoring
 * Practices "modal dialog" focus contract:
 *
 *   - Tab from the last focusable element cycles back to the first
 *   - Shift+Tab from the first focusable element cycles to the last
 *   - Focus stays inside the container while it is active
 *
 * Pattern: a global `keydown` listener is registered when `active` is
 * true. On every Tab press we recompute the focusable set (the set
 * can change mid-life -- e.g. a child renders a link asynchronously)
 * and redirect focus when the user would otherwise tab out.
 *
 * NOT exhaustive -- focusable selector covers the common HTML
 * tabbable elements (button / link / input / textarea / select /
 * [tabindex]). Custom widgets that opt into tabbability via
 * `tabIndex={0}` therefore participate; widgets that rely on
 * `contenteditable` do not (no current consumer needs them).
 *
 * Elements with `tabindex="-1"` or `disabled` are excluded so the
 * cycle skips them -- matches native browser tab behavior.
 *
 * @param containerRef Ref to the element whose subtree forms the trap.
 * @param active       Whether the trap is currently engaged.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const root = containerRef.current;
      if (!root) return;

      const focusables = getFocusableElements(root);
      if (focusables.length === 0) {
        // No focusables at all -- swallow Tab so focus can't escape.
        e.preventDefault();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        // Shift+Tab on the first element -> wrap to last.
        if (activeEl === first || !root.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab on the last element -> wrap to first.
        if (activeEl === last || !root.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [active, containerRef]);
}

/**
 * Return every tabbable element inside `root` in DOM order. Filters
 * out disabled controls and elements with `tabindex="-1"`.
 *
 * Exported (lib-private) for testability and for the rare consumer
 * who needs the focusable list outside the trap (e.g. moving focus
 * to the first interactive element on open).
 */
export function getFocusableElements(root: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
  return nodes.filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('tabindex') === '-1') return false;
    return true;
  });
}
